import { tzName } from "@date-fns/tz";
import { TIMEZONE_DATA, type Timezone } from "@hex/common";

import { getCurrentTimezone } from "./getTimezone";
import { MINIMAL_TIMEZONE_DATA } from "./timezoneItems";
import type { TimezoneWithNames } from "./timezoneTypes";

const CURRENT_DATE = new Date();

export function isValidTimezone(timeZone: string | undefined): boolean {
  if (timeZone === undefined) {
    return false;
  }
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch (_) {
    return false;
  }
}

export function getTimezoneShortName(
  tzIanaCode: string,
  date: Date | undefined,
): string {
  return tzName(tzIanaCode, date ?? CURRENT_DATE, "short");
}

export function getTimezoneLongName(
  tzIanaCode: string,
  date: Date | undefined,
): string {
  return tzName(tzIanaCode, date ?? CURRENT_DATE, "long");
}

/**
 * Augments a simple {@link Timezone} metadata object with long and short names formatted by `date-fns`.
 */
export function getTimezoneNames(
  tz: Timezone,
  date: Date | undefined = CURRENT_DATE,
): TimezoneWithNames {
  return {
    ...tz,
    shortName: getTimezoneShortName(tz.ianaCode, date),
    longName: getTimezoneLongName(tz.ianaCode, date),
  };
}

export const mapTimezonesWithNames = (
  date: Date | undefined,
  timezones: Timezone[] | TimezoneWithNames[],
): TimezoneWithNames[] => timezones.map((tz) => getTimezoneNames(tz, date));

export function getInitialTimezoneItems(
  date: Date | undefined,
  showLocalTimezone: boolean,
): TimezoneWithNames[] {
  const systemTimezone = getCurrentTimezone();
  const localTimezone = showLocalTimezone
    ? TIMEZONE_DATA.find((timezone) => timezone.ianaCode === systemTimezone)
    : undefined;
  const localTimezoneItem =
    localTimezone !== undefined
      ? {
          ...localTimezone,
          longName: "Current timezone",
          shortName: getTimezoneShortName(
            localTimezone.ianaCode,
            date ?? CURRENT_DATE,
          ),
        }
      : undefined;
  const minimalTimezoneItemsWithNames = mapTimezonesWithNames(
    date,
    MINIMAL_TIMEZONE_DATA,
  ).filter((tz) => tz.ianaCode !== localTimezoneItem?.ianaCode);
  return localTimezoneItem === undefined
    ? minimalTimezoneItemsWithNames
    : [localTimezoneItem, ...minimalTimezoneItemsWithNames];
}
